/*
 * Deterministic, flicker-free client-side experiment assignment.
 *
 * Pulls the experiment registry from the Worker (running experiments only),
 * caches it in localStorage (5-min TTL), and assigns each visitor a stable
 * variant via a hash of (visitorId, experiment key, salt). Assignment is pure
 * and deterministic, so the inline snippet (snippets/exp-assign.liquid) and
 * this engine compute the SAME variant without coordination.
 *
 * Fail-soft everywhere: any error → native experience (no assignment, no throw).
 * Flag-dark: an empty registry assigns nothing and renders nothing.
 *
 * Exposure is logged once per (visitor, experiment) through the existing
 * window.AISearchAnalytics.track(), deduped via a localStorage set.
 *
 * The pure logic (cyrb53, unitFloat, weightedPick, computeAssignments) is
 * exported for node --test; the browser build also attaches window.NeuroExp.
 */
(function () {
  'use strict';

  // ---- Pure assignment primitives (VERBATIM; also exported for tests) -------

  function cyrb53(str, seed = 0) {
    let h1 = 0xdeadbeef ^ seed, h2 = 0x41c6ce57 ^ seed;
    for (let i = 0; i < str.length; i++) {
      const ch = str.charCodeAt(i);
      h1 = Math.imul(h1 ^ ch, 2654435761);
      h2 = Math.imul(h2 ^ ch, 1597334677);
    }
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
    return 4294967296 * (2097151 & h2) + (h1 >>> 0);
  }
  function unitFloat(visitorId, key, salt) { return (cyrb53(visitorId + ':' + key + ':' + salt) % 1000000) / 1000000; }
  function weightedPick(u, variants) {
    const total = variants.reduce((s, v) => s + v.weight, 0); let acc = 0;
    for (const v of variants) { acc += v.weight / total; if (u < acc) return v.key; }
    return variants[variants.length - 1].key;
  }

  // Pure: given a registry + visitorId (+ optional page context), return
  // {expKey: variantKey} for each running experiment the visitor matches.
  // The Worker returns running-only rows; `mode` is 'experiment'|'monitor'
  // (monitors have no variants → skipped). Honors targeting:
  //   traffic_allocation (0..1): deterministic per-visitor inclusion fraction
  //   page_match (string): the current path must contain it (skipped if no path)
  // context = { path } — pass window.location.pathname in the browser.
  function computeAssignments(registry, visitorId, context) {
    const out = {};
    if (!registry || !visitorId) return out;
    const ctx = context || {};
    const exps = Array.isArray(registry.experiments) ? registry.experiments : [];
    for (const exp of exps) {
      try {
        if (!exp || exp.mode === 'monitor') continue;
        if (!exp.key || !Array.isArray(exp.variants) || exp.variants.length === 0) continue;
        const t = exp.targeting || {};
        if (t.page_match) {
          if (typeof ctx.path !== 'string' || ctx.path.indexOf(t.page_match) === -1) continue;
        }
        const salt = exp.salt == null ? '' : String(exp.salt);
        const alloc = (typeof t.traffic_allocation === 'number') ? t.traffic_allocation : 1;
        if (alloc < 1 && unitFloat(visitorId, exp.key + ':__traffic', salt) >= alloc) continue;
        const u = unitFloat(visitorId, exp.key, salt);
        out[exp.key] = weightedPick(u, exp.variants);
      } catch (e) { /* skip this experiment, never throw */ }
    }
    return out;
  }

  // Pure: experiment keys we previously stamped that are no longer assigned
  // (so their data-exp-* attribute should be removed on reconcile).
  function keysToRemove(stamped, assignments) {
    var out = [];
    for (var k in stamped) {
      if (Object.prototype.hasOwnProperty.call(stamped, k) &&
          !Object.prototype.hasOwnProperty.call(assignments, k)) out.push(k);
    }
    return out;
  }

  // ---- Browser-only registry/exposure plumbing ------------------------------
  // Guarded so this whole block is a no-op under node (the test runner).

  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    (function attachBrowser() {
      var cfg = window.AISearch || {};
      var SID_KEY = 'nai_sid';
      var EXP_KEY = 'nai_exp';            // cached registry {ts, registry}
      var SEEN_KEY = 'nai_exp_seen';      // logged exposures: {"key:visitor":1}
      var TTL = 5 * 60 * 1000;            // 5-min registry cache

      function store() { try { return window.localStorage; } catch (e) { return null; } }
      function ready() { return !!(cfg.enabled && cfg.url && cfg.tenant); }
      function uuid() {
        try { if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID(); } catch (e) {}
        return 'nai-' + Date.now().toString(36) + '-' + Math.floor(Math.random() * 1e9).toString(36);
      }
      // Get-or-create the durable visitor id. Creating it here (not just reading)
      // means plain page-viewers — who never trigger a search/cart event — still
      // get a stable id and get enrolled. Shares the 'nai_sid' key with
      // ai-search-analytics.js sessionId(); whichever runs first creates it.
      function visitorId() {
        var ls = store(); if (!ls) return null;
        try {
          var v = ls.getItem(SID_KEY);
          if (!v) { v = uuid(); ls.setItem(SID_KEY, v); }
          return v;
        } catch (e) { return null; }
      }
      var _stamped = {};  // expKey -> dataset camelKey, for cross-refresh reconciliation

      function pageContext() {
        try { return { path: (window.location && window.location.pathname) || '' }; } catch (e) { return {}; }
      }
      function endpoint() {
        return cfg.url.replace(/\/+$/, '') + '/api/experiments?tenant=' + encodeURIComponent(cfg.tenant);
      }

      function readCache() {
        var ls = store(); if (!ls) return null;
        try {
          var raw = ls.getItem(EXP_KEY); if (!raw) return null;
          var obj = JSON.parse(raw);
          if (!obj || typeof obj.ts !== 'number' || !obj.registry) return null;
          return obj;
        } catch (e) { return null; }
      }
      function writeCache(registry) {
        var ls = store(); if (!ls) return;
        try { ls.setItem(EXP_KEY, JSON.stringify({ ts: Date.now(), registry: registry })); } catch (e) {}
      }
      function fresh(cache) { return !!(cache && (Date.now() - cache.ts) < TTL); }

      // Fetch the running-experiments registry and cache it. Returns a promise
      // that resolves to the registry (or null). Never rejects.
      var _inflight = null;
      function refresh() {
        if (!ready()) return Promise.resolve(null);
        if (_inflight) return _inflight;
        try {
          _inflight = fetch(endpoint(), { method: 'GET', credentials: 'omit' })
            .then(function (r) { return r && r.ok ? r.json() : null; })
            .then(function (json) {
              if (!json || !Array.isArray(json.experiments)) return null;
              writeCache(json);
              return json;
            })
            .catch(function () { return null; })
            .then(function (r) { _inflight = null; return r; });
          return _inflight;
        } catch (e) { _inflight = null; return Promise.resolve(null); }
      }

      // The registry to assign against right now: cached if present (even stale,
      // so assignment stays consistent with what the inline snippet painted),
      // and kick off a background refresh when stale/missing.
      function currentRegistry() {
        var cache = readCache();
        if (!cache || !fresh(cache)) { refresh(); }
        return cache ? cache.registry : null;
      }

      function variantOf(key) {
        try {
          var vid = visitorId(); if (!vid || !key) return null;
          var registry = currentRegistry(); if (!registry) return null;
          var assignments = computeAssignments(registry, vid, pageContext());
          return Object.prototype.hasOwnProperty.call(assignments, key) ? assignments[key] : null;
        } catch (e) { return null; }
      }

      function featureOf(registry, key) {
        try {
          var exps = registry && Array.isArray(registry.experiments) ? registry.experiments : [];
          for (var i = 0; i < exps.length; i++) { if (exps[i] && exps[i].key === key) return exps[i].feature || ''; }
        } catch (e) {}
        return '';
      }

      function seenSet() {
        var ls = store(); if (!ls) return {};
        try { return JSON.parse(ls.getItem(SEEN_KEY) || '{}') || {}; } catch (e) { return {}; }
      }
      function markSeen(seen, dedupeKey) {
        var ls = store(); if (!ls) return;
        try { seen[dedupeKey] = 1; ls.setItem(SEEN_KEY, JSON.stringify(seen)); } catch (e) {}
      }

      // Log exposure once per (visitor, experiment). Deduped via nai_exp_seen.
      function logExposureOnce(key, variant, registry, vid) {
        try {
          if (!key || variant == null) return;
          var dedupeKey = key + ':' + vid;
          var seen = seenSet();
          if (seen[dedupeKey]) return;
          var track = window.AISearchAnalytics && window.AISearchAnalytics.track;
          if (typeof track !== 'function') return;  // analytics not loaded yet; retry next assignAll
          track({ event_type: 'exposure', feature: featureOf(registry, key), experiment_id: key, variant: variant });
          markSeen(seen, dedupeKey);
        } catch (e) {}
      }

      // Stamp variant data-attrs for the current assignment, REMOVE attrs for
      // experiments we previously stamped that are no longer running, and fire
      // deduped exposures — but only when doExpose (a FRESH registry, never a
      // stale cache, so no stale exposures are logged).
      function stampAndExpose(registry, vid, doExpose) {
        var assignments = computeAssignments(registry, vid, pageContext());
        var remove = keysToRemove(_stamped, assignments);
        for (var r = 0; r < remove.length; r++) {
          try { delete document.documentElement.dataset[_stamped[remove[r]]]; } catch (e) {}
          delete _stamped[remove[r]];
        }
        for (var key in assignments) {
          if (!Object.prototype.hasOwnProperty.call(assignments, key)) continue;
          var variant = assignments[key];
          try {
            var camel = 'exp' + key.charAt(0).toUpperCase() + key.slice(1);
            document.documentElement.dataset[camel] = variant;
            _stamped[key] = camel;
          } catch (e) {}
          if (doExpose) logExposureOnce(key, variant, registry, vid);
        }
        return assignments;
      }

      function assignAll() {
        try {
          var vid = visitorId(); if (!vid) return {};
          var cache = readCache();
          var isFresh = fresh(cache);
          // Stamp from cache immediately for flicker-free render, but EXPOSE only
          // when the cache is fresh — never log a stale exposure for a retired exp.
          if (cache && cache.registry) stampAndExpose(cache.registry, vid, isFresh);
          // Missing/stale cache: fetch, then stamp+expose+reconcile from fresh data.
          if (!cache || !isFresh) {
            refresh().then(function (reg) { try { if (reg) stampAndExpose(reg, vid, true); } catch (e) {} });
          }
          return (cache && cache.registry) ? computeAssignments(cache.registry, vid, pageContext()) : {};
        } catch (e) { return {}; }
      }

      window.NeuroExp = { variantOf: variantOf, assignAll: assignAll, refresh: refresh };

      // assignAll() is idempotent: data-attrs are re-set to the same value and
      // exposures are deduped (nai_exp_seen). Run it on multiple triggers so the
      // exposure fires even if this engine executes BEFORE ai-search-analytics.js:
      //  - immediately: stamp variant data-attrs (flicker-free render)
      //  - DOMContentLoaded + load: by then ai-search-analytics.js has executed and
      //    window.AISearchAnalytics.track exists, so the deferred exposure fires.
      function boot() { try { assignAll(); } catch (e) {} }
      boot();
      if (document.readyState !== 'complete') {
        window.addEventListener('DOMContentLoaded', boot, { once: true });
        window.addEventListener('load', boot, { once: true });
      }
    })();
  }

  // Export pure logic for node --test while still attaching to window above.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { cyrb53, unitFloat, weightedPick, computeAssignments, keysToRemove };
  }
})();
